import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Cardbenefits,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import Table from "../../../components/table/table-2";
import { createHealthCardMasterFields } from "../../../config/health/health-card-master";
import { isFormValid } from "../../../utils/validators";
import FormRenderer from "../../../components/input-field/form-renderer";
import { useEffect, useRef, useState } from "react";
import RichTextEditor from "../../../components/textEditor/richTextEditor";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  createHealthCard,
  deleteHealthCard,
  getHealthCard,
  updateHealthCard,
} from "../../../services/admin/cardService";
import Loader from "../../../components/loader/loader";
import { showErrorToast, showSuccessToast } from "../../../utils/toast-utils";
import FormHeading from "../../../components/branch/formHeading";
import { useQuery } from "@tanstack/react-query";
import ConfirmModal from "../../../components/confirmModal/confirm";
import { DeleteIcon, Edit, Trash, Trash2 } from "lucide-react";

const CreatePharmacy = () => {
  const [form, setForm] = useState({});
  const [benefits, setbenefits] = useState(null);
  const [creationResponse, setCreationResponse] = useState(null);
  const [updateResponse, setUpdateResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formType, setFormType] = useState("create");
  const [cardId, setCardId] = useState(null);
  const [editClicked, setEditClicked] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [cardForDelete, setCardForDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const cardColumns = [
    { key: "card_name", label: "Card Name" },
    { key: "price", label: "Price (₹)" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold
        ${
          row.status === "active"
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700"
        }`}
        >
          {row.status === "active" ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      key: "edit",
      label: "Edit",
      render: (row) => (
        <Button
          onClick={() => handleEdit(row)}
          className="bg-yellow-300 border border-yellow-600 hover:bg-green-300 text-yellow-900 h-8 px-4"
        >
          <Edit size={18} />
        </Button>
      ),
    },
    {
      key: "delete",
      label: "Delete",
      render: (row) => (
        <Button
          onClick={() => {
            setCardForDelete(row);
            setOpenConfirmationModal(true);
          }}
          className="0 bg-red-600 text-white hover:bg-red-700 h-8 px-4"
        >
          <Trash2 size={18} />
        </Button>
      ),
    },
  ];

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "arial",
    "roboto",
    "open-sans",
    "poppins",
    "montserrat",
    "lato",
    "times-new-roman",
    "georgia",
    "merriweather",
    "playfair",
    "courier-new",
    "fira-code",
    "oswald",
    "bebas",
    "pacifico",
  ];
  Quill.register(Font, true);

  /* ================= TOOLBAR ================= */
  const modules = {
    toolbar: [
      [{ font: Font.whitelist }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image", "code-block"],
      ["clean"],
    ],
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const res = await createHealthCard({
        name: form.card_name,
        price: form.price,
        benefits: benefits,
        duration: form.duration,
        status: form.status,
      });

      if (
        res?.data?.status === 200 ||
        res?.data?.status === 201 ||
        res?.data?.status === true
      ) {
        showSuccessToast(res?.data?.message || res?.data?.data?.message);
        setCreationResponse(res?.data);
        console.log(res);
        setForm({});
        setbenefits(null);
        await refetch();
      } else {
        showErrorToast(
          res?.data?.message ||
            res?.data?.data?.message ||
            "something went wrong",
        );
        console.log(res);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (row) => {
    console.log("clicked");
    setEditClicked(true);

    setCreationResponse(null);
    setUpdateResponse(null);

    setFormType("update");
    setForm({
      card_name: row.card_name,
      status: row?.status,
      price: row.price,
      duration: row.duration_in_months,
    });

    setbenefits(row?.benefits);

    setCardId(row.id);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await updateHealthCard({
        cardId,
        name: form.card_name,
        price: form.price,
        benefits: benefits,
        duration: form.duration,
        status: form.status,
      });

      if (res) {
        console.log(res);
        setUpdateResponse(res);
        await refetch();
      }

      if (
        res?.status === 200 ||
        res?.data?.status === 200 ||
        res?.data?.status === true
      ) {
        setForm({});
        setbenefits("");
        showSuccessToast(
          res?.message || res?.data?.message || "card Updated successfully",
        );
      } else {
        showErrorToast(
          res?.message || res?.data?.message || "something went wrong",
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);

    try {
      const res = await deleteHealthCard({
        cardId: cardForDelete.id,
      });

      if (res) {
        console.log(res);
        await refetch();
      }

      if (
        res?.status === 200 ||
        res?.data?.status === 200 ||
        res?.data?.status === true
      ) {
        showSuccessToast(
          res?.message || res?.data?.message || "card deleted successfully",
        );
      } else {
        showErrorToast("something went wrong");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setOpenConfirmationModal(false);
    }
  };

  const {
    data: cardData,
    isLoading: cardLoading,
    refetch,
  } = useQuery({
    queryKey: ["all-cards", currentPage],
    queryFn: async () => {
      const res = await getHealthCard({ page: currentPage });
      return res?.data;
    },
  });

  useEffect(() => {
    if (!editClicked) return;

    const element = document.getElementById("scrollIntoView");

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    const timer = setTimeout(() => {
      setEditClicked(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [editClicked]);

  return (
    <div className=" min-h-screen">
      <div id="scrollIntoView" className="h-1" />

      {(loading || cardLoading) && <Loader />}
      <FormHeading
        subtitle={
          formType === "create"
            ? "Add a new health card"
            : "Update card details"
        }
        title={
          formType === "create"
            ? "Create New Health Card"
            : "Update Health Card"
        }
        formType={formType}
      />

      <div className="grid grid-cols-1 mt-2 gap-8">
        <Card className=" rounded-xl bg-white  border-none ">
          <div className="w-full p-6 flex flex-col">
            <div className="w-full">
              <div className="w-full mb-4">
                <FormRenderer
                  fields={createHealthCardMasterFields}
                  values={form}
                  setValues={setForm}
                />
              </div>

              <ReactQuill
                value={benefits}
                onChange={setbenefits}
                modules={modules}
                style={{
                  height: "200px",
                  padding: "10px",
                  borderRadius: "10px",
                }}
                theme="snow"
                // className="compact-quill"
                placeholder="Write your card benefits here..."
              />

              <div className="mt-20">
                <Button
                  onClick={formType === "create" ? handleCreate : handleUpdate}
                  disabled={
                    !isFormValid(createHealthCardMasterFields, form) ||
                    !benefits
                  }
                  variant="admin.secondary"
                  className={`${formType === "update" && "bg-yellow-500 text-black"}`}
                >
                  {formType === "create" ? "Create Card" : "Update Card"}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-xl border-none bg-white p-2">
          <CardHeader>
            <CardTitle className="text-md md:text-xl text-gray-600">
              Health Card List
            </CardTitle>
          </CardHeader>
          <CardContent className=" text-[#081a3a] text-2xl md:text-4xl font-bold">
            <Table
              columns={cardColumns}
              data={cardData?.data}
              rowsPerPage={4}
              headerBg="bg-[#0a1f44]"
              headerText="text-white"
            />

            {cardData?.pagination?.total_pages > 1 && (
              <div className="flex justify-center gap-4 pt-4 border-t ">
                <Button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  variant="admin.secondary"
                >
                  Prev
                </Button>

                <span className="text-sm mt-2">
                  Page {currentPage} of {cardData?.pagination?.total_pages}
                </span>

                <Button
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(p + 1, cardData?.pagination?.total_pages),
                    )
                  }
                  disabled={currentPage === cardData?.pagination?.total_pages}
                  variant="admin.secondary"
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmModal
        title={`Cofirm action to delete ${cardForDelete?.card_name}`}
        isOpen={openConfirmationModal}
        onCancel={() => setOpenConfirmationModal(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default CreatePharmacy;
